<template>
  <div class="modal fade" id="budgetModal" tabindex="-1">
    <div class="modal-dialog modal-xl">
      <div class="modal-content" :key="uniqueKey">
        <div class="modal-header bg-light">
          <div>
            <h5 class="modal-title mb-0">{{ isEditMode ? 'Editar orçamento' : 'Novo orçamento' }}</h5>
            <span class="text-muted fs-7">Monte o orçamento com paciente, forma de pagamento e itens</span>
          </div>
          <button type="button" class="btn-close" @click="closeModal"></button>
        </div>

        <form @submit.prevent="submit">
          <div class="modal-body">
            <div class="row g-4">
              <div class="col-12">
                <BudgetPatientSection
                    :patient-id="patientId"
                    :patient="patient"
                    :selected-label="patientLabel"
                    @select="selectPatient"
                    @new="setNewPatient"/>
              </div>

              <div class="col-md-6">
                <label class="form-label required">Tipo</label>
                <select class="form-select form-select-solid" v-model="form.budget_type" required>
                  <option :value="null">Selecione</option>
                  <option v-for="type in budgetTypes" :key="type.value" :value="type.value">
                    {{ type.label }}
                  </option>
                </select>
              </div>

              <div class="col-md-6">
                <label class="form-label required">Forma de pagamento</label>
                <select class="form-select form-select-solid" v-model="form.id_paymeth" required>
                  <option :value="null">Selecione</option>
                  <option v-for="paymentMethod in paymentMethods" :key="paymentMethod.id_paymeth" :value="paymentMethod.id_paymeth">
                    {{ paymentMethod.paymeth_name }}
                  </option>
                </select>
              </div>

              <div class="col-md-6" v-if="isInsurancePayment">
                <label class="form-label required">Convênio</label>
                <select class="form-select form-select-solid" v-model="form.id_insurance" required>
                  <option :value="null">Selecione</option>
                  <option v-for="insurance in insurances" :key="insurance.id_insurance" :value="insurance.id_insurance">
                    {{ insurance.trading_name ? `${insurance.trading_name} - ${insurance.insurance_name}` : insurance.insurance_name }}
                  </option>
                </select>
              </div>

              <div class="col-md-4">
                <label class="form-label">Validade</label>
                <input class="form-control form-control-solid" type="date" v-model="form.budget_valid_until">
                <span class="text-muted fs-8">Em branco: orçamento sem prazo.</span>
              </div>

              <div class="col-md-4">
                <label class="form-label">Local de atendimento</label>
                <select class="form-select form-select-solid" v-model="form.id_place_location">
                  <option :value="null">{{ place.place_name }} (principal)</option>
                  <option v-for="location in locations" :key="location.id_place_location" :value="location.id_place_location">
                    {{ location.location_nickname || location.location_name }}
                  </option>
                </select>
              </div>

              <div class="col-md-4">
                <label class="form-label">Prestador</label>
                <select class="form-select form-select-solid" v-model="form.id_prof">
                  <option :value="null">Sem prestador definido</option>
                  <option v-for="professional in professionals" :key="professional.id_prof" :value="professional.id_prof">
                    {{ professional.prof_name }}
                  </option>
                </select>
              </div>

              <div class="col-12">
                <label class="form-label">Observação</label>
                <textarea class="form-control form-control-solid" rows="2" v-model="form.budget_obs"></textarea>
              </div>

              <div class="col-12">
                <BudgetItemsEditor v-model="items" :budget-type="form.budget_type"/>
              </div>

              <div class="col-md-6">
                <label class="form-label">Desconto</label>
                <div class="input-group">
                  <select class="form-select form-select-solid w-auto flex-grow-0" v-model="discountType" style="max-width: 130px">
                    <option value="value">R$</option>
                    <option value="percent">%</option>
                  </select>
                  <input class="form-control form-control-solid" type="number" min="0" step="0.01"
                         :max="discountType === 'percent' ? 100 : subtotal"
                         v-model.number="discountAmount">
                </div>
                <span class="text-danger fs-8" v-if="discountError">{{ discountError }}</span>
              </div>

              <div class="col-md-6">
                <div class="bg-light rounded p-4">
                  <div class="d-flex justify-content-between mb-2">
                    <span class="text-muted">Subtotal</span>
                    <span>R$ {{ subtotal.toFixed(2) }}</span>
                  </div>
                  <div class="d-flex justify-content-between mb-2 text-success" v-if="discountValue > 0">
                    <span>Desconto{{ discountType === 'percent' ? ` (${discountAmount}%)` : '' }}</span>
                    <span>- R$ {{ discountValue.toFixed(2) }}</span>
                  </div>
                  <div class="separator my-2"></div>
                  <div class="d-flex justify-content-between fw-bold fs-4">
                    <span>Total</span>
                    <span>R$ {{ total.toFixed(2) }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="modal-footer d-flex justify-content-between">
            <div class="fw-bold fs-5">Total: R$ {{ total.toFixed(2) }}</div>
            <div>
              <button type="button" class="btn btn-light me-2" @click="closeModal">Cancelar</button>
              <button type="submit" class="btn btn-primary" :disabled="saving">
                {{ saving ? 'Salvando...' : (isEditMode ? 'Salvar orçamento' : 'Criar orçamento') }}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { patientIdentity } from "../../helpers/patientIdentity";
import http from "@/helpers/http";
import Modal from "@/helpers/modal";
import Alert from "@/helpers/alert";
import BudgetPatientSection from "@/views/budget/BudgetPatientSection.vue";
import BudgetItemsEditor from "@/views/budget/BudgetItemsEditor.vue";

export default {
  name: "BudgetModal",
  components: {BudgetItemsEditor, BudgetPatientSection},
  props: ['budget'],
  emits: ['saved', 'onClose'],
  data() {
    return {
      budgetTypes: [],
      paymentMethods: [],
      insurances: [],
      locations: [],
      professionals: [],
      discountType: 'value',
      discountAmount: null,
      patientId: null,
      patientLabel: '',
      patient: {
        patient_name: '',
        patient_cpf: '',
        patient_phone: '',
      },
      form: this.emptyForm(),
      items: [],
      saving: false,
      uniqueKey: Date.now().toString(),
    };
  },
  computed: {
    place() {
      return this.$store.state.auth.place;
    },
    isEditMode() {
      return !!this.budget?.id_budget;
    },
    isInsurancePayment() {
      return !!this.paymentMethods.find(method => method.id_paymeth === this.form.id_paymeth)?.is_insurance;
    },
    subtotal() {
      return this.items.reduce((sum, item) => (
          sum + Number(item.item_value || 0) * Math.max(1, Number(item.item_quantity ?? 1))
      ), 0);
    },
    // Desconto em percentual acompanha o subtotal; a API refaz a conta.
    discountValue() {
      const amount = Number(this.discountAmount || 0);
      if (amount <= 0) return 0;
      if (this.discountType === 'percent') return Math.round(this.subtotal * Math.min(amount, 100)) / 100;
      return Math.min(amount, this.subtotal);
    },
    total() {
      return Math.max(0, this.subtotal - this.discountValue);
    },
    discountError() {
      const amount = Number(this.discountAmount || 0);
      if (this.discountType === 'percent' && amount > 100) return 'O percentual não pode passar de 100%.';
      if (this.discountType === 'value' && amount > this.subtotal) return 'O desconto não pode ser maior que os itens.';
      return '';
    },
  },
  watch: {
    budget: {
      immediate: true,
      handler() {
        this.populate();
        this.uniqueKey = Date.now().toString();
      },
    },
    'form.id_paymeth'(value, previous) {
      if (previous == null || previous === value || this.isInsurancePayment) return;
      this.form.id_insurance = null;
    },
  },
  mounted() {
    this.loadOptions();
  },
  methods: {
    emptyForm() {
      return {
        id_paymeth: null,
        id_insurance: null,
        budget_type: null,
        budget_obs: '',
        budget_valid_until: '',
        id_place_location: null,
        id_prof: null,
      };
    },
    loadOptions() {
      http.get(`/places/${this.place.id_place}/budgets/types`).then(response => {
        this.budgetTypes = Object.entries(response.data).map(([key, label]) => ({
          key,
          label,
          value: this.typeValue(key),
        }));
      });
      http.get(`/places/${this.place.id_place}/payment-methods`).then(response => {
        this.paymentMethods = response.data;
      });
      http.get(`/places/${this.place.id_place}/insurances`).then(response => {
        this.insurances = response.data?.data ?? response.data ?? [];
      });
      http.get(`/places/${this.place.id_place}/locations`).then(response => {
        this.locations = (response.data?.data ?? response.data ?? []).filter(location => location.active !== false);
      });
      http.get(`/places/${this.place.id_place}/professionals`).then(response => {
        this.professionals = response.data?.data ?? response.data ?? [];
      });
    },
    populate() {
      if (!this.budget?.id_budget) {
        this.patientId = null;
        this.patientLabel = '';
        this.patient = {patient_name: '', patient_cpf: '', patient_phone: ''};
        this.form = this.emptyForm();
        this.items = [];
        this.discountType = 'value';
        this.discountAmount = null;
        return;
      }

      this.patientId = this.budget.id_patient;
      this.patientLabel = this.budget.patient?.patient_name ?? '';
      this.patient = {
        patient_name: this.budget.patient?.patient_name ?? '',
        patient_cpf: this.budget.patient?.patient_cpf ?? '',
        patient_phone: this.budget.patient?.patient_phone ?? '',
      };
      this.form = {
        id_paymeth: this.budget.id_paymeth,
        id_insurance: this.budget.id_insurance ?? null,
        budget_type: this.budget.budget_type,
        budget_obs: this.budget.budget_obs ?? '',
        budget_valid_until: this.budget.budget_valid_until ?? '',
        id_place_location: this.budget.id_place_location ?? null,
        id_prof: this.budget.id_prof ?? null,
      };
      this.discountType = Number(this.budget.budget_discount_percent || 0) > 0 ? 'percent' : 'value';
      this.discountAmount = this.discountType === 'percent'
          ? Number(this.budget.budget_discount_percent)
          : (Number(this.budget.budget_discount || 0) || null);
      this.items = (this.budget.budget_items ?? []).map(item => ({
        id_item: item.id_item,
        description: this.itemLabel(item.item),
        item_value: Number(item.item_value),
        item_quantity: Number(item.item_quantity ?? 1),
      }));
    },
    typeValue(key) {
      if (key === 'exam_lab') return 'EXAM-LAB';
      if (key === 'exam_occ') return 'EXAM-OCC';
      return key.toUpperCase();
    },
    itemLabel(item) {
      const exam = item?.exam;
      if (exam) return [exam.exam_tuss_code, exam.exam_name].filter(Boolean).join(' - ');
      return item?.item_name ?? 'Item';
    },
    selectPatient(patient) {
      this.patientId = patient.id_patient;
      this.patientLabel = patient.patient_name;
      this.patient = {
        patient_name: patient.patient_name,
        patient_cpf: patient.patient_cpf,
        patient_phone: patient.patient_phone,
      };
    },
    setNewPatient(patient) {
      this.patientId = -1;
      this.patientLabel = patient.patient_name;
      this.patient = patient;
    },
    payload() {
      const amount = Number(this.discountAmount || 0);

      return {
        id_paymeth: this.form.id_paymeth,
        id_insurance: this.isInsurancePayment ? this.form.id_insurance : null,
        budget_type: this.form.budget_type,
        budget_obs: this.form.budget_obs,
        budget_valid_until: this.form.budget_valid_until || null,
        id_place_location: this.form.id_place_location || null,
        id_prof: this.form.id_prof || null,
        budget_discount: this.discountType === 'value' ? amount : 0,
        budget_discount_percent: this.discountType === 'percent' ? amount : 0,
        items: this.items.map(item => ({
          id_item: item.id_item,
          item_value: Number(item.item_value),
          item_quantity: Math.max(1, Number(item.item_quantity ?? 1)),
        })),
        ...(this.patientId && this.patientId !== -1
            ? {id_patient: this.patientId}
            : {patient: this.cleanPatient()}),
      };
    },
    cleanPatient() {
      return {
        ...patientIdentity(this.patient),
        patient_name: this.patient.patient_name,
        patient_cpf: String(this.patient.patient_cpf ?? '').replace(/\D/g, ''),
        patient_phone: String(this.patient.patient_phone ?? '').replace(/\D/g, ''),
      };
    },
    submit() {
      if (!this.items.length) return Alert.toastWarning('Adicione pelo menos um item.');
      if (this.isInsurancePayment && !this.form.id_insurance) return Alert.toastWarning('Informe o convênio médico.');
      if (this.discountError) return Alert.toastWarning(this.discountError);
      this.saving = true;
      const request = this.isEditMode
          ? http.put(`/places/${this.place.id_place}/budgets/${this.budget.id_budget}`, this.payload())
          : http.post(`/places/${this.place.id_place}/budgets`, this.payload());

      request.then(() => {
        Alert.toastSuccess('Orçamento salvo com sucesso!');
        this.$emit('saved');
        this.closeModal();
      }).catch(e => {
        Alert.error(e.response?.data?.message ?? e.response?.data);
      }).finally(() => {
        this.saving = false;
      });
    },
    closeModal() {
      Modal.hide('#budgetModal');
      this.$emit('onClose');
    },
  },
}
</script>
